// Social Media Automation Service
import { ClaudePostGenerator } from './claudePostGenerator';
import { socialMediaService } from './socialMediaService';
import { SocialPost, PostGenerationRequest } from '../types/social.types';

export class SocialMediaAutomation {
  private generator: ClaudePostGenerator;
  private isRunning: boolean = false;
  
  constructor(apiKey: string) {
    this.generator = new ClaudePostGenerator(apiKey);
  }

  // Uruchom automatyzację
  async startAutomation() {
    if (this.isRunning) return;
    this.isRunning = true;
    
    console.log('🤖 Automatyzacja Social Media uruchomiona');
    
    // Sprawdzaj co godzinę
    setInterval(() => {
      this.checkAndCreatePosts();
    }, 60 * 60 * 1000); // Co godzinę
    
    // Pierwsza publikacja
    this.checkAndCreatePosts();
  }

  // Zatrzymaj automatyzację
  stopAutomation() {
    this.isRunning = false;
    console.log('🛑 Automatyzacja Social Media zatrzymana');
  }

  // Główna logika automatyzacji
  private async checkAndCreatePosts() {
    const now = new Date();
    const hour = now.getHours();
    
    // Najlepsze godziny publikacji
    const optimalHours = [9, 13, 19]; // 9:00, 13:00, 19:00
    
    if (!optimalHours.includes(hour)) return;
    
    try {
      // 1. Analizuj co jest popularne
      const trendingTopics = await this.analyzeTrends();
      
      // 2. Wygeneruj post na podstawie trendów
      const post = await this.generateSmartPost(trendingTopics);
      
      // 3. Opublikuj na wszystkich platformach
      await this.publishPost(post);
      
      console.log(`✅ Post opublikowany automatycznie o ${hour}:00`);
    } catch (error) {
      console.error('❌ Błąd automatyzacji:', error);
    }
  }

  // Analizuj trendy i tematy
  private async analyzeTrends(): Promise<string[]> {
    const topics = [
      'ekspozytory reklamowe',
      'stojaki na ulotki', 
      'gabloty informacyjne',
      'oznakowanie biura',
      'plexi antybakteryjne',
      'ekologiczne rozwiązania',
      'personalizacja produktów'
    ];
    
    // Wybierz losowy temat + sezonowość
    const month = new Date().getMonth();
    const seasonalTopics: Record<number, string[]> = {
      0: ['noworoczne promocje', 'planowanie roku'],
      1: ['walentynkowe dekoracje'],
      2: ['wiosenne nowości'],
      3: ['wielkanocne ekspozytory'],
      4: ['komunijne dekoracje'],
      5: ['letnie promocje'],
      6: ['wakacyjne rabaty'],
      7: ['powrót do szkoły'],
      8: ['jesienne trendy'],
      9: ['halloween dekoracje'],
      10: ['black friday'],
      11: ['świąteczne ekspozytory']
    };
    
    const seasonal = seasonalTopics[month] || [];
    return [...topics, ...seasonal];
  }

  // Generuj inteligentny post
  private async generateSmartPost(topics: string[]): Promise<SocialPost> {
    // Wybierz losowy temat
    const topic = topics[Math.floor(Math.random() * topics.length)];
    
    // Określ typ postu na podstawie dnia tygodnia
    const dayOfWeek = new Date().getDay();
    const postTypes: Record<number, { tone: PostGenerationRequest['tone'], type: string }> = {
      0: { tone: 'friendly', type: 'inspiracja' }, // Niedziela
      1: { tone: 'professional', type: 'porada' }, // Poniedziałek
      2: { tone: 'informative', type: 'edukacja' }, // Wtorek
      3: { tone: 'professional', type: 'case study' }, // Środa
      4: { tone: 'enthusiastic', type: 'nowość' }, // Czwartek
      5: { tone: 'friendly', type: 'za kulisami' }, // Piątek
      6: { tone: 'casual', type: 'weekend' } // Sobota
    };
    
    const { tone, type } = postTypes[dayOfWeek];
    
    // Generuj post
    const request: PostGenerationRequest = {
      topic: `${type}: ${topic}`,
      tone,
      length: 'medium',
      platforms: ['facebook', 'linkedin', 'instagram'],
      includeHashtags: true,
      includeEmojis: true,
      productMention: topic.includes('ekspozytor') ? 'Ekspozytory A4' : undefined,
      callToAction: this.getRandomCTA()
    };
    
    const generated = await this.generator.generatePost(request);
    
    // Stwórz post w systemie
    const post = await socialMediaService.createPost({
      content: generated.content,
      images: [], // TODO: Dodaj automatyczne generowanie obrazów
      platforms: request.platforms,
      status: 'draft',
      ai_generated: true,
      created_by: 'automation',
      metadata: {
        hashtags: generated.hashtags,
        topic,
        type,
        automatedPost: true
      }
    });
    
    return post;
  }

  // Losowe wezwanie do działania
  private getRandomCTA(): string {
    const ctas = [
      'Sprawdź naszą ofertę!',
      'Skontaktuj się z nami!',
      'Zamów bezpłatną wycenę!',
      'Zobacz więcej na stronie!',
      'Napisz do nas!',
      'Zadzwoń i zapytaj o szczegóły!',
      'Odwiedź nasz showroom!'
    ];
    
    return ctas[Math.floor(Math.random() * ctas.length)];
  }

  // Publikuj post
  private async publishPost(post: SocialPost) {
    await socialMediaService.publishPost({
      post_id: post.id,
      platforms: post.platforms
    });
  }

  // Generuj raport tygodniowy
  async generateWeeklyReport(): Promise<string> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    const stats = await socialMediaService.getAggregatedStats(startDate, endDate);
    
    const report = `
📊 RAPORT TYGODNIOWY SOCIAL MEDIA
================================

📅 Okres: ${startDate.toLocaleDateString('pl-PL')} - ${endDate.toLocaleDateString('pl-PL')}

STATYSTYKI:
• Opublikowane posty: ${stats.totalPosts}
• Całkowity zasięg: ${stats.totalReach.toLocaleString('pl-PL')}
• Zaangażowanie: ${stats.totalEngagement.toLocaleString('pl-PL')}
• Średnie zaangażowanie: ${stats.avgEngagementRate}%

TOP POSTY:
${stats.topPerformingPosts.map((post, i) => 
  `${i + 1}. "${post.content.substring(0, 50)}..." - ${new Date(post.created_at).toLocaleDateString('pl-PL')}`
).join('\n')}

REKOMENDACJE:
${stats.avgEngagementRate < 2 ? '⚠️ Zaangażowanie poniżej 2% - zwiększ interakcję z obserwatorami' : '✅ Dobre zaangażowanie'}
${stats.totalPosts < 21 ? '⚠️ Publikuj częściej - cel to 3 posty dziennie' : '✅ Dobra częstotliwość publikacji'}

--
Wygenerowano automatycznie przez PlexiSystem AI
    `;
    
    return report;
  }
}

// Singleton dla automatyzacji
let automationInstance: SocialMediaAutomation | null = null;

export const startSocialMediaAutomation = (apiKey: string) => {
  if (!automationInstance) {
    automationInstance = new SocialMediaAutomation(apiKey);
  }
  automationInstance.startAutomation();
  return automationInstance;
};

export const getSocialMediaAutomation = () => automationInstance;
